package top.wcpe.allincore.ui.runtime.render

import top.wcpe.allincore.layout.engine.Box
import top.wcpe.allincore.ui.runtime.element.Switch
import top.wcpe.allincore.ui.runtime.element.UIElement
import top.wcpe.allincore.ui.runtime.render.UiRenderBridge
import top.wcpe.allincore.ui.runtime.render.model.RenderBatch
import top.wcpe.allincore.ui.runtime.render.model.RenderCommand
import top.wcpe.allincore.ui.runtime.render.model.RenderCommandType
import top.wcpe.allincore.ui.runtime.render.model.StyledFill
import top.wcpe.allincore.ui.runtime.style.CornerRadii
import top.wcpe.allincore.ui.runtime.theme.StateLayerCompositor
import top.wcpe.allincore.ui.runtime.theme.ThemeAccess
import kotlin.math.max

/**
 * 开关渲染器（接入主题，Task 2a 支持逐项覆盖）。
 * - 轨道：off=trackColorOff（覆盖 > surfaceContainerHighest），on=trackColorOn（覆盖 > primary）；填充整个 box；
 *   trackCornerRadius>0 时圆角
 * - 滑块：方块尺寸 = box.height - 2*thumbInset（覆盖 > 2）；off 贴左、on 贴右；
 *   off=thumbColorOff（覆盖 > outline），on=thumbColorOn（覆盖 > onPrimary）；thumbCornerRadius>0 时圆角
 * - 状态层：逐态覆盖优先，否则按 thumb 颜色 + 主题透明度合成
 */
@Suppress("MagicNumber")
class SwitchRenderer : Renderer {

    override fun render(element: UIElement<*>, box: Box, bridge: UiRenderBridge) {
        val sw = element as Switch
        val t = ThemeAccess.current()
        val on = sw.enabled()

        // 轨道
        val track = if (on) sw.trackColorOn() ?: t.primary else sw.trackColorOff() ?: t.surfaceContainerHighest
        val trackRadius = sw.trackCornerRadius() ?: 0f
        if (trackRadius > 0f) {
            fillRounded(bridge, box.x(), box.y(), box.width(), box.height(), track, trackRadius)
        } else {
            bridge.fillRect(box.x(), box.y(), box.width(), box.height(), track)
        }

        // 滑块
        val pad = sw.thumbInset() ?: 2f
        val thumbSize = max(0f, box.height() - 2f * pad)
        val thumbX = if (on) box.x() + box.width() - thumbSize - pad else box.x() + pad
        val thumbY = box.y() + pad
        val thumbColor = if (on) sw.thumbColorOn() ?: t.onPrimary else sw.thumbColorOff() ?: t.outline
        val thumbRadius = sw.thumbCornerRadius() ?: 0f
        if (thumbRadius > 0f) {
            fillRounded(bridge, thumbX, thumbY, thumbSize, thumbSize, thumbColor, thumbRadius)
        } else {
            bridge.fillRect(thumbX, thumbY, thumbSize, thumbSize, thumbColor)
        }

        // 状态层：在 thumb 上叠加，pressed > focused > hovered。逐态覆盖优先，否则按 thumb 同色基底。
        val overlayBase = if (on) t.onPrimary else t.onSurface
        val overrides = StateLayerCompositor.overrides(
            resolveStateColor(sw.hoverOverlayColor(), overlayBase),
            sw.hoverOverlayOpacity(),
            resolveStateColor(sw.focusOverlayColor(), overlayBase),
            sw.focusOverlayOpacity(),
            resolveStateColor(sw.pressedOverlayColor(), overlayBase),
            sw.pressedOverlayOpacity(),
        )
        StateLayerCompositor.overlay(bridge, sw, thumbX, thumbY, thumbSize, thumbSize, t, false, overrides, true)
    }

    /** 逐态覆盖色：DSL 指定则用之，否则回退到默认基底色。 */
    private fun resolveStateColor(override: Int?, fallback: Int): Int = override ?: fallback

    /** 圆角矩形填充：走 ROUNDED_RECT styled batch。 */
    private fun fillRounded(bridge: UiRenderBridge, x: Float, y: Float, w: Float, h: Float, color: Int, radius: Float) {
        val fill = StyledFill.builder()
            .cornerRadii(CornerRadii.uniform(radius).toArray())
            .borderWidth(0f)
            .borderColor(0)
            .build()
        val batch = RenderBatch(
            RenderCommandType.ROUNDED_RECT,
            null,
            RenderCommand.NO_CLIP,
            listOf(RenderCommand.roundedRect(x, y, w, h, color, RenderCommand.NO_CLIP, fill)),
        )
        bridge.submitBatch(batch)
    }
}
